import { Plugin } from 'ckeditor5';
import { v4 as uuid } from 'uuid';
import { ConditionState } from './State';
import { InsertConditionCommand } from './insertCommand';
import './style.css';
export class ConditionsSchema extends Plugin {
    static get pluginName() {
        return 'ConditionsSchema';
    }
    init() {
        this.state = this.editor.plugins.get(ConditionState);
        this.defineSchema();
        this.defineConverters();
        this.defineCommands();
    }
    defineCommands() {
        const { editor } = this;
        editor.commands.add(InsertConditionCommand.pluginName, new InsertConditionCommand(editor));
    }
    defineSchema() {
        const { schema } = this.editor.model;
        schema.extend('$text', {
            allowAttributes: ['conditionMarker'],
        });
    }
    defineConverters() {
        const { editor } = this;
        // Map of condition IDs to instance IDs for upcast conversion
        // In the HTML source conditions can share the same ID, this is however not supported by CKEditor markers.
        // To work around this we generate a unique instance ID for each condition on upcast.
        const conditionInstanceMap = new Map();
        /**
         * UPCAST: HTML → Model
         * Converts <condition-start> tags from HTML
         * into condition markers in the model.
         */
        editor.conversion.for('upcast').elementToMarker({
            view: 'condition-start',
            model: (viewElement) => {
                const id = viewElement.getAttribute('name');
                const instanceId = uuid();
                const markerName = `condition-inline:${id}:${instanceId}`;
                conditionInstanceMap.set(id, instanceId);
                this.state.addCondition(markerName);
                return markerName;
            },
        });
        /**
         * UPCAST: HTML → Model
         * Converts <condition-end> tags from HTML
         * into condition markers in the model.
         */
        editor.conversion.for('upcast').elementToMarker({
            view: 'condition-end',
            model: (viewElement) => {
                const id = viewElement.getAttribute('name');
                const instanceId = conditionInstanceMap.get(id);
                conditionInstanceMap.delete(id);
                return `condition-inline:${id}:${instanceId}`;
            },
        });
        /**
         * EDITING DOWNCAST: Model → View (Editor)
         * Converts condition markers in the model
         * into condition labels in the view.
         */
        editor.conversion.for('editingDowncast').markerToElement({
            model: 'condition-inline',
            view: (markerData, { writer }) => {
                const parts = markerData.markerName.split(':');
                const [, type, id] = parts;
                const conditionId = `${type}:${id}`;
                const className = markerData.isOpening
                    ? 'condition-label condition-label--start'
                    : 'condition-label condition-label--end';
                return writer.createUIElement('span', {
                    class: className,
                    'data-condition-id': conditionId,
                    'data-marker-name': markerData.markerName,
                    'data-start': markerData.isOpening,
                });
            },
        });
        /**
         * DATA DOWNCAST: Model → HTML
         * Converts condition markers in the model
         * into <condition-start> and <condition-end> tags in HTML.
         */
        editor.conversion.for('dataDowncast').markerToElement({
            model: 'condition-inline',
            view: (markerData, { writer }) => {
                const { markerName } = markerData;
                // Extract condition ID, ignoring instance ID
                const parts = markerName.split(':');
                const [, type, id] = parts;
                const name = `${type}:${id}`;
                if (markerData.isOpening) {
                    return writer.createEmptyElement('condition-start', { name });
                }
                return writer.createEmptyElement('condition-end', { name });
            },
        });
    }
}
